import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { REMARK_PLUGINS } from "@/lib/markdown";
import { Link } from "react-router-dom";
import { Eye, Edit3, Trash2, Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";

const EMPTY = {
  slug: "",
  title: "",
  meta_description: "",
  cover_image: "",
  content_md: "## New page\n\nWrite your content here.",
  published: true,
};

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

export default function PagesPanel({ initialSlug }) {
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [original, setOriginal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const isNew = selected === null;

  const load = () =>
    api.get("/pages").then((r) => {
      setPages(r.data);
    });

  useEffect(() => {
    load();
  }, []);

  // Deep-link: once pages have loaded, open the requested slug if present.
  useEffect(() => {
    if (!initialSlug || pages.length === 0) return;
    if (pages.some((p) => p.slug === initialSlug)) {
      setSelected(initialSlug);
    }
  }, [initialSlug, pages]);

  useEffect(() => {
    if (selected) {
      const p = pages.find((x) => x.slug === selected);
      if (p) {
        const next = {
          slug: p.slug,
          title: p.title,
          meta_description: p.meta_description || "",
          cover_image: p.cover_image || "",
          content_md: p.content_md || "",
          published: p.published !== false,
        };
        setDraft(next);
        setOriginal(next);
      }
    }
  }, [selected, pages]);

  const dirty = useMemo(() => {
    if (isNew) return draft.title.trim().length > 0;
    if (!original) return false;
    return (
      draft.title !== original.title ||
      draft.meta_description !== original.meta_description ||
      draft.cover_image !== original.cover_image ||
      draft.content_md !== original.content_md ||
      draft.published !== original.published ||
      draft.slug !== original.slug
    );
  }, [draft, original, isNew]);

  const startNew = () => {
    setSelected(null);
    setDraft(EMPTY);
    setOriginal(null);
    setShowPreview(false);
  };

  const save = async () => {
    if (!draft.title.trim() || !draft.content_md.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: draft.title.trim(),
        meta_description: draft.meta_description.trim(),
        cover_image: draft.cover_image.trim(),
        content_md: draft.content_md,
        published: draft.published,
      };
      if (isNew) {
        payload.slug = (draft.slug || slugify(draft.title)).trim();
        const { data } = await api.post("/pages", payload);
        toast.success(`Saved "${data.title}"`);
        await load();
        setSelected(data.slug);
      } else {
        if (draft.slug && draft.slug !== selected) payload.slug = draft.slug;
        const { data } = await api.put(`/pages/${selected}`, payload);
        toast.success(`Saved "${data.title}"`);
        await load();
        setSelected(data.slug);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete page "${draft.title}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/pages/${selected}`);
      toast.success("Deleted");
      await load();
      startNew();
    } catch {
      toast.error("Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="grid lg:grid-cols-[260px_1fr] gap-4"
      data-testid="admin-pages-panel"
    >
      <aside className="space-y-2">
        <button
          type="button"
          onClick={startNew}
          data-testid="page-new-btn"
          className={`w-full brut px-3 py-3 font-bold uppercase tracking-wide text-sm brut-press inline-flex items-center justify-center gap-2 ${
            isNew ? "pcs-bg-pink text-white" : "bg-white"
          }`}
        >
          <Plus className="w-4 h-4" strokeWidth={3} />
          New page
        </button>

        <div className="brut bg-white divide-y-2 divide-black/10 max-h-[70vh] overflow-y-auto">
          {pages.length === 0 ? (
            <div className="p-3 font-mono uppercase text-[10px] tracking-widest opacity-60">
              No pages yet
            </div>
          ) : (
            pages.map((p) => (
              <button
                type="button"
                key={p.slug}
                onClick={() => setSelected(p.slug)}
                data-testid={`page-list-${p.slug}`}
                className={`w-full text-left p-3 hover:pcs-bg-cream transition-colors ${
                  selected === p.slug ? "pcs-bg-cream" : ""
                }`}
              >
                <div className="font-bold text-sm">{p.title}</div>
                <div className="mt-1 flex items-center gap-2 font-mono uppercase text-[10px] tracking-widest opacity-70">
                  <span
                    className={`px-1.5 ${
                      p.published ? "pcs-bg-pink text-white" : "bg-gray-200"
                    }`}
                  >
                    {p.published ? "live" : "draft"}
                  </span>
                  <span className="truncate">/page/{p.slug}</span>
                </div>
              </button>
            ))
          )}
        </div>
      </aside>

      <div className="brut bg-white p-4 md:p-5 space-y-3">
        <div className="flex flex-wrap items-center gap-2 pb-3 border-b-2 border-black">
          <div className="font-display text-2xl tracking-tight">
            {isNew ? "Create page" : `Edit · ${draft.title || "(untitled)"}`}
          </div>
          <button
            type="button"
            onClick={() => setShowPreview((s) => !s)}
            data-testid="page-toggle-preview"
            className="ml-auto brut bg-white px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press inline-flex items-center gap-1"
          >
            {showPreview ? <Edit3 className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {showPreview ? "Edit" : "Preview"}
          </button>
          {!isNew && (
            <Link
              to={`/page/${selected}`}
              target="_blank"
              data-testid="page-view-live"
              className="brut bg-white px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press inline-flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              View
            </Link>
          )}
          <label className="inline-flex items-center gap-1.5 font-mono uppercase text-[10px] tracking-widest cursor-pointer">
            <input
              type="checkbox"
              checked={draft.published}
              onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
              data-testid="page-published-toggle"
              className="w-4 h-4 border-2 border-black"
            />
            Published
          </label>
          {!isNew && (
            <button
              type="button"
              onClick={del}
              disabled={busy}
              data-testid="page-delete"
              className="brut bg-white px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press disabled:opacity-50 inline-flex items-center gap-1"
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          )}
          <button
            type="button"
            onClick={save}
            disabled={busy || !dirty}
            data-testid="page-save"
            className="brut pcs-bg-pink text-white px-3 py-1.5 font-bold uppercase text-xs tracking-wide brut-press disabled:opacity-50"
          >
            {busy ? "Saving..." : isNew ? "Save page" : dirty ? "Save changes" : "Saved"}
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
              Title *
            </label>
            <input
              value={draft.title}
              onChange={(e) => {
                setDraft({
                  ...draft,
                  title: e.target.value,
                  slug: isNew ? slugify(e.target.value) : draft.slug,
                });
              }}
              data-testid="page-title"
              className="w-full brut bg-white px-3 py-2 focus:outline-none"
            />
          </div>
          <div>
            <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
              Slug
            </label>
            <input
              value={draft.slug}
              onChange={(e) => setDraft({ ...draft, slug: slugify(e.target.value) })}
              data-testid="page-slug"
              className="w-full brut bg-white px-3 py-2 focus:outline-none font-mono text-xs"
            />
          </div>
        </div>

        <div>
          <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
            Meta description (SEO)
          </label>
          <input
            value={draft.meta_description}
            onChange={(e) => setDraft({ ...draft, meta_description: e.target.value })}
            placeholder="Shown by Google & social previews. Aim for 150-160 chars."
            data-testid="page-meta"
            className="w-full brut bg-white px-3 py-2 focus:outline-none text-sm"
          />
        </div>

        <div>
          <ImageUploader
            label="Cover image"
            value={draft.cover_image}
            onChange={(url) => setDraft({ ...draft, cover_image: url })}
            testId="page-cover"
            hint="Used by some pages as a hero image (e.g. _articles-hero)."
            aspect="aspect-[16/9]"
          />
        </div>

        <div>
          <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
            Content (Markdown) *
          </label>
          {showPreview ? (
            <div
              className="brut bg-white p-4 prose prose-sm max-w-none min-h-[300px]"
              data-testid="page-preview"
            >
              <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{draft.content_md}</ReactMarkdown>
            </div>
          ) : (
            <textarea
              value={draft.content_md}
              onChange={(e) => setDraft({ ...draft, content_md: e.target.value })}
              rows={18}
              data-testid="page-content"
              className="w-full brut bg-white px-3 py-2 focus:outline-none font-mono text-xs resize-y"
            />
          )}
        </div>
      </div>
    </div>
  );
}
